import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, CalendarDays, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Full-month ex-dividend calendar for the currently scoped portfolio.
 *
 * Renders a 7-column grid (Sun..Sat) for the visible month, with per-day chips
 * for every ex-div event. Past events are muted; upcoming events are highlighted
 * in the primary accent. Header has ‹ › month nav + a "Today" reset. Hovering
 * a chip shows the estimated payment.
 */
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmt$ = (v) =>
  v == null || v === 0 ? "" :
  `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DividendCalendar({ portfolioId }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() }; // month is 0-indexed
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { months_back: 6 };
        if (portfolioId) params.portfolio_id = portfolioId;
        const r = await api.get("/holdings/ex-div-calendar", { params });
        if (!cancelled) setEvents(r.data.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [portfolioId]);

  const monthKey = `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}`;
  const monthEvents = useMemo(
    () => events.filter((e) => e.ex_date.startsWith(monthKey)),
    [events, monthKey],
  );
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of monthEvents) {
      (map[e.ex_date] ||= []).push(e);
    }
    return map;
  }, [monthEvents]);

  const days = useMemo(() => {
    // Build a 6-row grid: leading blanks + days + trailing blanks (up to 42 cells).
    const firstDay = new Date(cursor.year, cursor.month, 1);
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const startWeekday = firstDay.getDay(); // 0..6
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${monthKey}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, date });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor, monthKey]);

  const totalPayments = monthEvents.reduce(
    (s, e) => s + Math.max(0, Number(e.estimated_payment || 0)),
    0,
  );

  const prev = () => setCursor((c) => c.month === 0
    ? { year: c.year - 1, month: 11 }
    : { ...c, month: c.month - 1 });
  const next = () => setCursor((c) => c.month === 11
    ? { year: c.year + 1, month: 0 }
    : { ...c, month: c.month + 1 });
  const today = () => {
    const d = new Date();
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
  };

  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <Card data-testid="dividend-calendar">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-primary" />
            <h3 className="serif text-xl">Ex-dividend calendar</h3>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={prev} data-testid="calendar-prev" className="rounded-full h-8 w-8 p-0" aria-label="Previous month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="mono text-xs uppercase tracking-widest px-2 min-w-[130px] text-center" data-testid="calendar-month-label">
              {MONTH_NAMES[cursor.month]} {cursor.year}
            </div>
            <Button variant="ghost" size="sm" onClick={next} data-testid="calendar-next" className="rounded-full h-8 w-8 p-0" aria-label="Next month">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={today} data-testid="calendar-today" className="rounded-full ml-1 mono text-[10px] uppercase">
              Today
            </Button>
          </div>
        </div>

        {loading && (
          <div className="h-32 grid place-items-center text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {!loading && (
          <>
            <div className="grid grid-cols-7 gap-1 mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1">
              {WEEKDAYS.map((w, i) => (
                <div key={i} className="text-center py-1">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
              {days.map((cell, idx) => {
                if (!cell) return <div key={idx} className="h-16 rounded-md bg-muted/10 border border-transparent" />;
                const dayEvents = eventsByDate[cell.date] || [];
                const isToday = cell.date === todayStr;
                return (
                  <div
                    key={idx}
                    className={`h-16 rounded-md border p-1 overflow-hidden text-left ${
                      dayEvents.length > 0
                        ? "border-primary/30 bg-primary/[0.03]"
                        : "border-border/60 bg-card"
                    } ${isToday ? "ring-1 ring-primary" : ""}`}
                    data-testid={`calendar-day-${cell.date}`}
                  >
                    <div className={`mono text-[10px] ${isToday ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                      {cell.day}
                    </div>
                    <div className="mt-0.5 space-y-0.5">
                      {dayEvents.slice(0, 2).map((e, i) => (
                        <div
                          key={`${e.ticker}-${i}`}
                          title={`${e.ticker} · ${e.kind === "past" ? "paid" : "upcoming"}${e.estimated_payment ? ` · ${fmt$(e.estimated_payment)}` : ""}`}
                          className={`mono text-[9px] leading-tight truncate rounded px-1 py-0.5 ${
                            e.kind === "past"
                              ? "bg-muted text-muted-foreground"
                              : "bg-primary/15 text-primary font-semibold"
                          }`}
                          data-testid={`calendar-event-${e.ex_date}-${e.ticker}`}
                        >
                          {e.ticker}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="mono text-[9px] text-muted-foreground pl-1">+{dayEvents.length - 2}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded bg-primary/50" /> Upcoming
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded bg-muted-foreground/60" /> Paid
                </span>
              </div>
              {totalPayments > 0 && (
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="calendar-month-total">
                  {MONTH_NAMES[cursor.month].slice(0, 3)} total: <span className="text-primary">{fmt$(totalPayments)}</span>
                </div>
              )}
            </div>

            {monthEvents.length > 0 && (
              <div className="mt-3 border-t border-border pt-3">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">This month ({monthEvents.length})</div>
                <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {monthEvents.map((e, i) => (
                    <li key={`${e.ex_date}-${e.ticker}-${i}`} className="flex items-center justify-between text-xs" data-testid={`calendar-list-${e.ex_date}-${e.ticker}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge variant="outline" className="mono text-[9px] uppercase whitespace-nowrap">{e.kind}</Badge>
                        <span className="mono font-semibold">{e.ticker}</span>
                        <span className="mono text-muted-foreground">{e.ex_date}</span>
                      </div>
                      {e.estimated_payment > 0 && (
                        <span className={`mono ${e.kind === "past" ? "text-muted-foreground" : "text-primary"}`}>
                          {e.kind === "past" ? "" : "≈ "}{fmt$(e.estimated_payment)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {monthEvents.length === 0 && (
              <div className="mt-4 text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">
                No ex-dividend events in {MONTH_NAMES[cursor.month]} {cursor.year}.
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
